import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Skeleton, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ChatApi, IChat, LanguageLevelEnum } from "../api/ChatApi";
import AddIcon from "../asset/icon-add-black.png";
import ChatIcon from "../asset/icon-chat.png";
import DeleteIcon from '../asset/icon-delete.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import ChatListContext from "../context/ChatListContext";

interface OfficeHourMenuProps {
    isMenuCollapsed: boolean;
    onToggleMenuCollapse: () => void;
    isMobile: boolean;
    isTouchDevice: boolean;
}

export default function OfficeHourMenu({ isMenuCollapsed, onToggleMenuCollapse, isMobile, isTouchDevice }: OfficeHourMenuProps) {
    const { chatList, setChatList, isLoading, setIsLoading, setShowSkeleton, selectedChat, setSelectedChat } = React.useContext(ChatListContext);
    const { setErrorMessage } = React.useContext(AppBarContext);

    const history = useHistory();
    const location = useLocation();

    const [chatToDelete, setChatToDelete] = React.useState<IChat | null>(null);
    const [openDialog, setOpenDialog] = React.useState(false);


    React.useEffect(() => {
        setTimeout(() => {
            setShowSkeleton(true);
        }, 300); // 300ms delay before showing shimmer

        setIsLoading(true)

        ChatApi.getAllOfficeHourChat()
            .then((response) => {
                if (response.status < 300) {
                    return response.data;
                }
                handleErrorMessage(response, setErrorMessage, history, location);
                return [];
            })
            .then((list) => setChatList(list.sort((a, b) => b.id - a.id)))
            .catch((err) => {
                if (err.response && err.response.status == 492) {
                    setErrorMessage("This user is read only and cannot delete.");
                } else {
                    handleErrorMessage(err, setErrorMessage, history, location);
                }
            })
            .finally(() => {
                setIsLoading(false);
                setShowSkeleton(false);
            });
    }, [history, location, setChatList, setErrorMessage, setIsLoading, setShowSkeleton]);

    const handleListSelect = (chat: IChat) => {
        setSelectedChat(chat);
        if (isMobile || isTouchDevice) {
            onToggleMenuCollapse()
        }
    };

    const handleAddChat = () => {
        setSelectedChat({ id: -1, language_level: LanguageLevelEnum.Hsk1, first_line: "AddChat", latest_chat_date: new Date().toISOString(), });
        if (isMobile || isTouchDevice) {
            onToggleMenuCollapse()
        }
    };
    const handleOpenDialog = (chat: IChat) => {
        setChatToDelete(chat);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setChatToDelete(null);
    };

    const confirmDelete = () => {
        if (chatToDelete) {
            ChatApi.deleteOfficeHourChat(chatToDelete.id)
                .then(() => {
                    // Remove from chat list
                    setChatList(chatList.filter(c => c.id !== chatToDelete.id));

                    // Only clear right side if the deleted chat is currently selected
                    if (selectedChat && selectedChat.id === chatToDelete.id) {
                        setSelectedChat({ id: -1, language_level: LanguageLevelEnum.Hsk1, first_line: "AddChat", latest_chat_date: new Date().toISOString(), });
                    }

                    handleCloseDialog();
                })
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                });
        }
    };

    const getChatGroupTitle = (chatDate: Date) => {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (
            chatDate.getFullYear() === today.getFullYear() &&
            chatDate.getMonth() === today.getMonth() &&
            chatDate.getDate() === today.getDate()
        ) {
            return { main: "Today", subtitle: chatDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) };
        } else if (
            chatDate.getFullYear() === yesterday.getFullYear() &&
            chatDate.getMonth() === yesterday.getMonth() &&
            chatDate.getDate() === yesterday.getDate()
        ) {
            return { main: "Yesterday", subtitle: chatDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) };
        } else {
            return { main: "Older", subtitle: chatDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) };
        }
    };

    const groupChatsByDate = (chats: IChat[]) => {
        const sortedChats = [...chats]
            .filter(chat => chat.latest_chat_date)
            .sort((a, b) =>
                new Date(b.latest_chat_date!).getTime() - new Date(a.latest_chat_date!).getTime()
            );

        const groups: { [key: string]: IChat[] } = {};

        sortedChats.forEach(chat => {
            const chatDate = new Date(chat.latest_chat_date || new Date());
            const { main } = getChatGroupTitle(chatDate);
            if (!groups[main]) groups[main] = [];
            groups[main].push(chat);
        });

        const order = ["Today", "Yesterday", "Older"];
        return order
            .filter(key => groups[key])
            .map(key => ({ main: key, chats: groups[key] }));
    };

    if (isMenuCollapsed) return (<></>)
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box display="flex" sx={{ pt: 2, pl: 2, flexShrink: 0 }}>
                <Typography variant="h2" color="secondary">
                    Prior Chats
                </Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <Box display="flex" flexDirection="column">
                    <List sx={{ width: "100%", maxWidth: "600px" }}>
                            {isLoading
                                ? Array.from({ length: 5 }).map((_, i) => (
                                    <Box key={i} sx={{ display: "flex", mb: 3, mt: 2, alignItems: "center" }}>
                                        <Skeleton variant="rectangular" height={24} width={24} sx={{ borderRadius: 1, backgroundColor: "#F2F7FF" }} />
                                        <Skeleton variant="rectangular" height={20} width={256} sx={{ borderRadius: 1, backgroundColor: "#F2F7FF", ml: 4 }} />
                                    </Box>
                                    ))
                                : chatList.length === 0
                                    ? (
                                        <Typography sx={{ color: "#fff", px: 2, mt: 2 }}>
                                        No chats yet.
                                        </Typography>
                                    )
                            : groupChatsByDate(chatList).map(group => (
                                <Box key={group.main} sx={{ mb: 2, px: 2 }}>
                                    {/* Group Header */}
                                    <Typography variant="h4" sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                                        {group.main}
                                    </Typography>

                                    {/* Individual Chats */}
                                    {group.chats.map(chat => (
                                        <ListItem
                                            key={chat.id}
                                            sx={{ p: 0, my: 2, width: "100%" }}
                                            secondaryAction={
                                                <div onClick={() => handleOpenDialog(chat)}>
                                                    <IconButton edge="end" aria-label="delete">
                                                        <img src={DeleteIcon} alt="Delete" style={{ width: "24px", height: "24px" }} />
                                                    </IconButton>
                                                </div>
                                            }
                                        >
                                            <ListItemButton sx={{ p: 0 }} onClick={() => handleListSelect(chat)}>
                                                <ListItemIcon>
                                                    <img src={ChatIcon} alt="Chat" style={{ width: "24px", height: "24px" }} />
                                                </ListItemIcon>

                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            sx={{
                                                                color: "common.white",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {chat.first_line || "..."}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography
                                                            sx={{
                                                                color: "#888",
                                                                fontSize: 12,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {(chat.latest_chat_date
                                                                ? new Date(chat.latest_chat_date)
                                                                : new Date()
                                                            ).toLocaleDateString(undefined, {
                                                                weekday: "short",
                                                                month: "short",
                                                                day: "numeric",
                                                            })}{" "}
                                                            {(chat.latest_chat_date
                                                                ? new Date(chat.latest_chat_date)
                                                                : new Date()
                                                            ).toLocaleTimeString([], {
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            })}
                                                        </Typography>
                                                    }
                                                />
                                                
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </Box>
                            ))}
                    </List>
                </Box>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
                <Button variant="contained" sx={{ width: "100%", p: "12px", mb: 2, mt: 2 }} className="secondaryButton" onClick={handleAddChat}>
                    <img src={AddIcon} alt="Chat" style={{ width: "24px", height: "24px", marginRight: "16px" }} />
                    Start a new chat
                </Button>
            </Box>
            <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    <Typography variant="h4" sx={{ color: "#383838" }}>
                        {"Delete Chat?"}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: "#8D8D8D" }}>
                        Are you sure you want to delete this chat?
                        <br />
                        {chatToDelete && chatToDelete.first_line}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", "& .MuiButton-root": { flex: 1 } }}>
                    <Button onClick={handleCloseDialog} sx={{ color: "#787878" }}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} autoFocus variant="contained" color="error" sx={{ color: "#fff7f7" }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
